import { createContext, useContext, useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

const parseError = async (response) => {
  try {
    const data = await response.json();
    return data?.detail || "Operazione non riuscita";
  } catch {
    return "Operazione non riuscita";
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth/me-optional`, { credentials: "include" });
        if (!response.ok) {
          setUser(false);
          return;
        }
        const data = await response.json();
        setUser(data?.authenticated ? data.user : false);
      } catch {
        setUser(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkSession();
  }, []);

  const value = useMemo(() => ({
    user,
    isChecking,
    login: async (payload) => {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await parseError(response));
      }
      const data = await response.json();
      setUser(data);
      return data;
    },
    logout: async () => {
      await fetch(`${API_BASE}/api/auth/logout`, { method: "POST", credentials: "include" });
      setUser(false);
    },
    refreshProfile: async () => {
      const response = await fetch(`${API_BASE}/api/auth/me`, { credentials: "include" });
      if (!response.ok) {
        setUser(false);
        return null;
      }
      const data = await response.json();
      setUser(data);
      return data;
    },
  }), [isChecking, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};